import { useState } from 'react'
import { FileText, ExternalLink, ChevronDown, ChevronUp } from 'lucide-react'

interface PageData {
  url: string
  keywordCount: number
  bestPosition: number | null
  avgPosition: number | null
}

interface PagesSectionProps {
  pages: PageData[]
}

type PageTab = 'top' | 'analyzed' | 'insight'

const PAGE_TABS: { key: PageTab; label: string }[] = [
  { key: 'top', label: 'TOP' },
  { key: 'analyzed', label: 'ANALYSES' },
  { key: 'insight', label: 'EN VUE' },
]

function positionBadge(pos: number | null) {
  if (pos === null) return <span className="text-xs text-[#D1D5DB]">--</span>
  let cls = 'bg-gray-100 text-gray-500'
  if (pos === 1) cls = 'bg-emerald-500/15 text-emerald-500 font-bold'
  else if (pos <= 3) cls = 'bg-green-500/15 text-green-600'
  else if (pos <= 10) cls = 'bg-blue-500/15 text-blue-600'
  else if (pos <= 30) cls = 'bg-yellow-500/15 text-yellow-600'
  return (
    <span className={`inline-flex items-center justify-center min-w-[26px] px-2 py-0.5 rounded-lg text-xs font-semibold tabular-nums ${cls}`}>
      {pos}
    </span>
  )
}

function avgBadge(pos: number | null) {
  if (pos === null) return <span className="text-xs text-[#D1D5DB]">--</span>
  const rounded = Math.round(pos)
  let cls = 'bg-gray-100 text-gray-500'
  if (rounded <= 1) cls = 'bg-emerald-500/15 text-emerald-500'
  else if (rounded <= 3) cls = 'bg-green-500/15 text-green-600'
  else if (rounded <= 10) cls = 'bg-blue-500/15 text-blue-600'
  else if (rounded <= 30) cls = 'bg-yellow-500/15 text-yellow-600'
  return (
    <span className={`inline-flex items-center justify-center min-w-[26px] px-2 py-0.5 rounded-lg text-xs font-semibold tabular-nums ${cls}`}>
      {pos.toFixed(1)}
    </span>
  )
}

function truncateUrl(url: string, max = 50): string {
  const clean = url.replace(/^https?:\/\//, '').replace(/\/$/, '')
  if (clean.length <= max) return clean
  return clean.slice(0, max) + '...'
}

export default function PagesSection({ pages }: PagesSectionProps) {
  const [expanded, setExpanded] = useState(false)
  const [activeTab, setActiveTab] = useState<PageTab>('top')

  const visiblePages = expanded ? pages : pages.slice(0, 5)
  const hasMore = pages.length > 5

  return (
    <div className="bg-white border border-[#E2E4ED] rounded-xl p-5">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <div className="flex items-center justify-center w-8 h-8 rounded-lg bg-orion-primary/10">
            <FileText size={16} className="text-orion-primary" strokeWidth={1.25} />
          </div>
          <h3 className="text-sm font-semibold text-[#1B2559]">Pages</h3>
          {pages.length > 0 && (
            <span className="text-xs text-[#8B8FA7] tabular-nums">{pages.length}</span>
          )}
        </div>
      </div>

      <div className="flex items-center gap-1 mb-4">
        {PAGE_TABS.map(tab => {
          const active = activeTab === tab.key
          const disabled = tab.key !== 'top'
          return (
            <button
              key={tab.key}
              onClick={() => !disabled && setActiveTab(tab.key)}
              disabled={disabled}
              className={`px-3 py-1.5 rounded-lg text-[10px] font-bold uppercase tracking-wider transition-colors ${
                active
                  ? 'bg-orion-primary text-white'
                  : disabled
                    ? 'bg-[#F4F5FA] text-[#B0B4C5] cursor-not-allowed'
                    : 'bg-[#F4F5FA] text-[#555A73] hover:bg-[#E2E4ED]'
              }`}
            >
              {tab.label}
            </button>
          )
        })}
      </div>

      {pages.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-8 text-center">
          <div className="flex items-center justify-center w-10 h-10 rounded-xl bg-[#F4F5FA] mb-2.5">
            <FileText size={18} className="text-[#D1D5DB]" strokeWidth={1.25} />
          </div>
          <p className="text-sm text-[#8B8FA7]">Aucune page classee</p>
        </div>
      ) : (
        <>
          <div className="overflow-x-auto -mx-5">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-[#E2E4ED]">
                  <th className="text-left text-[10px] font-semibold text-[#8B8FA7] uppercase tracking-wider pb-2 px-5">URL</th>
                  <th className="text-center text-[10px] font-semibold text-[#8B8FA7] uppercase tracking-wider pb-2 px-3 w-16">KW</th>
                  <th className="text-center text-[10px] font-semibold text-[#8B8FA7] uppercase tracking-wider pb-2 px-3 w-20">Best</th>
                  <th className="text-center text-[10px] font-semibold text-[#8B8FA7] uppercase tracking-wider pb-2 px-5 w-20">Moy.</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-[#E2E4ED]/50">
                {visiblePages.map(page => (
                  <tr key={page.url} className="hover:bg-[#F4F5FA]/50 transition-colors">
                    <td className="py-2.5 px-5">
                      <a
                        href={page.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center gap-1 text-[#1B2559] hover:text-orion-primary transition-colors group"
                        title={page.url}
                      >
                        <span className="truncate max-w-[260px] text-xs">{truncateUrl(page.url)}</span>
                        <ExternalLink
                          size={11}
                          className="text-[#D1D5DB] group-hover:text-orion-primary shrink-0 transition-colors"
                          strokeWidth={1.25}
                        />
                      </a>
                    </td>
                    <td className="py-2.5 px-3 text-center">
                      <span className="inline-flex items-center justify-center min-w-[22px] px-1.5 py-0.5 rounded-md bg-orion-primary/10 text-[10px] font-bold text-orion-primary tabular-nums">
                        {page.keywordCount}
                      </span>
                    </td>
                    <td className="py-2.5 px-3 text-center">{positionBadge(page.bestPosition)}</td>
                    <td className="py-2.5 px-5 text-center">{avgBadge(page.avgPosition)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {hasMore && (
            <button
              onClick={() => setExpanded(!expanded)}
              className="flex items-center justify-center gap-1.5 w-full mt-3 pt-3 border-t border-[#E2E4ED] text-xs font-medium text-orion-primary hover:text-orion-primary-hover transition-colors"
            >
              {expanded ? (
                <>Reduire <ChevronUp size={13} strokeWidth={1.5} /></>
              ) : (
                <>Voir tout ({pages.length}) <ChevronDown size={13} strokeWidth={1.5} /></>
              )}
            </button>
          )}
        </>
      )}
    </div>
  )
}
